import { useEffect, useMemo, useState, lazy, Suspense } from "react";
import { useLocation } from "react-router-dom";
import { resolveProjectByDomain, slugify } from "@/lib/blogDomainResolver";
import BlogLoadingSkeleton from "@/components/blog/BlogLoadingSkeleton";
import { getCache, setCache } from "@/lib/blog-cache";
import { embeddedForCurrentHost } from "@/lib/public-blog-embedded";

const PublicBlog = lazy(() => import("./PublicBlog"));
const PublicBlogPost = lazy(() => import("./PublicBlogPost"));
const PublicBlogMateriais = lazy(() => import("./PublicBlogMateriais"));
const PublicBlogCategory = lazy(() => import("./PublicBlogCategory"));

function RobotsTxt() {
  const domain = window.location.hostname;
  const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
  const sitemapUrl = `https://${projectId}.supabase.co/functions/v1/blog-sitemap?host=${encodeURIComponent(domain)}`;
  const content = `User-agent: *\nAllow: /\nDisallow: /dashboard\nDisallow: /admin\n\nSitemap: ${sitemapUrl}`;
  return (
    <pre style={{ fontFamily: "monospace", whiteSpace: "pre-wrap" }}>
      {content}
    </pre>
  );
}

function SitemapXml({ host }: { host: string }) {
  const [xml, setXml] = useState<string>("");
  useEffect(() => {
    const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
    fetch(`https://${projectId}.supabase.co/functions/v1/blog-sitemap?host=${encodeURIComponent(host)}`)
      .then((r) => r.text())
      .then(setXml)
      .catch(() => setXml("<!-- sitemap unavailable -->"));
  }, [host]);
  return (
    <pre style={{ fontFamily: "monospace", whiteSpace: "pre-wrap", margin: 0 }}>
      {xml}
    </pre>
  );
}

function brandColorsOf(project: any) {
  return {
    menuBg: project.blog_color_menu_bg || undefined,
    menuFont: project.blog_color_menu_font || undefined,
    heroBg: project.blog_color_hero_bg || undefined,
    heroFont: project.blog_color_hero_font || undefined,
    gridBg: project.blog_color_grid_bg || undefined,
    font: project.blog_font_family || undefined,
  };
}

export default function CustomDomainRouter() {
  const location = useLocation();
  const hostname = typeof window !== "undefined" ? window.location.hostname : "";

  // A página servida pelo domínio próprio já traz o projeto embutido: não há
  // consulta nem esqueleto antes de abrir o blog.
  const embedded = useMemo(() => embeddedForCurrentHost(), []);
  const [resolvedProject, setResolvedProject] = useState<any>(embedded?.project ?? null);
  const [resolvedSlug, setResolvedSlug] = useState<string | null>(
    embedded?.project ? slugify(embedded.project.blog_name || embedded.project.name || "") : null,
  );
  const [loading, setLoading] = useState(!embedded?.project);
  const [notFound, setNotFound] = useState(false);

  const cachedColors = hostname ? getCache<any>(`blog_colors_host_${hostname}`) : null;

  useEffect(() => {
    const domain = window.location.hostname;
    if (embedded?.project) {
      try {
        setCache(`blog_colors_host_${domain}`, brandColorsOf(embedded.project), 24 * 60 * 60 * 1000);
      } catch {}
      return;
    }
    const resolve = async () => {
      const project = await resolveProjectByDomain(domain);
      if (!project) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      const slug = slugify(project.blog_name || project.name);
      setResolvedSlug(slug);
      setResolvedProject(project);
      try {
        setCache(`blog_colors_host_${domain}`, brandColorsOf(project), 24 * 60 * 60 * 1000);
      } catch {}
      setLoading(false);
    };
    resolve();
  }, [embedded]);

  if (loading) {
    return <BlogLoadingSkeleton colors={cachedColors || undefined} />;
  }

  if (notFound || !resolvedSlug) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <h1 className="text-2xl font-bold">Blog não encontrado</h1>
        <p className="text-muted-foreground">
          Nenhum blog está configurado para este domínio.
        </p>
      </div>
    );
  }

  let path = location.pathname.replace(/^\/+/, "").replace(/\/+$/, "");

  // Páginas numeradas das listagens: /pagina/2 e /category/x/pagina/2
  let initialPage = 1;
  const pageMatch = path.match(/^(.*?)\/?pagina\/(\d+)$/i);
  if (pageMatch) {
    path = pageMatch[1].replace(/^\/+/, "").replace(/\/+$/, "");
    initialPage = Math.max(1, parseInt(pageMatch[2], 10) || 1);
  } else {
    const qp = parseInt(new URLSearchParams(location.search).get("page") || "", 10);
    if (qp > 1) initialPage = qp;
  }

  if (path === "robots.txt") {
    return <RobotsTxt />;
  }

  if (path === "sitemap.xml") {
    return <SitemapXml host={window.location.hostname} />;
  }

  const skeletonVariant = path === "" ? "grid" : path === "materiais" ? "materiais" : path.startsWith("category/") ? "category" : "post";

  return (
    <Suspense fallback={<BlogLoadingSkeleton variant={skeletonVariant} colors={cachedColors || undefined} />}>
      {(() => {
        if (path === "" || path === "/") {
          return <PublicBlog customDomainSlug={resolvedSlug} resolvedProject={resolvedProject} initialPage={initialPage} />;
        }
        if (path === "materiais") {
          return <PublicBlogMateriais customDomainSlug={resolvedSlug} resolvedProject={resolvedProject} />;
        }
        if (path.startsWith("category/")) {
          const catSlug = path.replace("category/", "");
          return <PublicBlogCategory customDomainSlug={resolvedSlug} customCategorySlug={catSlug} resolvedProject={resolvedProject} initialPage={initialPage} />;
        }
        return <PublicBlogPost customDomainSlug={resolvedSlug} customPostSlug={path} resolvedProject={resolvedProject} />;
      })()}
    </Suspense>
  );
}
